import { useAccountRoster } from '@/hooks/account/useAccountRoster';
import { useBrandKits } from '@/hooks/useBrandKits';
import { firstNameOf } from '@/lib/format-name';

export interface CurrentContext {
  brandName: string | null;
  role: 'owner' | 'member' | null;
  isResolved: boolean;
  ownerFirstName: string | null;
  /** @deprecated org-model multi-brand list — orgs/org_members are retired.
   *  Always empty; kept only so existing callers/tests don't need a shape
   *  change. Multi-brand context now belongs to the workspace switcher. */
  orgs: { id: string; name: string }[];
  /** @deprecated see `orgs` — always false now. */
  isMultiContext: boolean;
  isLoading: boolean;
}

/**
 * Read-only "where am I working" context for the avatar surface.
 *
 * Three axes are kept independent: brand presence, account role, org membership.
 * `role` stays null until the roster query is fetched — it is NEVER defaulted to
 * 'owner', so a shared-account member is never transiently mislabeled as the
 * owner. `brandName` resolves to the OWNER's kit for a member via owner-scoped
 * `brand_kits` RLS (`user_id = current_account_owner_id()`), so no owner
 * resolution is added here.
 *
 * Phase-1 rule: this is a read-only LABEL, not an interactive account switcher.
 */
export function useCurrentContext(): CurrentContext {
  const { activeKit, isFetched: brandFetched, isLoading: brandLoading } = useBrandKits();
  const roster = useAccountRoster();

  const rosterFetched = roster.isFetched;
  const role: 'owner' | 'member' | null = !rosterFetched
    ? null
    : roster.data?.isOwner
      ? 'owner'
      : 'member';

  const ownerFirstName = role === 'member' ? firstNameOf(roster.data?.ownerProfile ?? null) : null;

  return {
    brandName: activeKit?.name ?? null,
    role,
    isResolved: rosterFetched && brandFetched,
    ownerFirstName,
    orgs: [],
    isMultiContext: false,
    isLoading: (brandLoading && !brandFetched) || roster.isLoading,
  };
}
